import type { Attributes, Span } from "@opentelemetry/api";
import { SpanStatusCode, trace } from "@opentelemetry/api";

const TRACER_NAME = "web-client";

/**
 * Span attribute the consuming app's head sampler routes on. Request spans are category `api`.
 * Must stay in sync with `TELEMETRY_CATEGORY_ATTRIBUTE` in `@storyboard/web`'s
 * `services/telemetry/categories.ts` — drift silently reroutes API spans to the default bucket.
 */
export const TELEMETRY_CATEGORY_ATTRIBUTE = "telemetry.category";

/**
 * Static, low-cardinality description of one API request, stamped by the generated operation that
 * issues it. The request's own path is interpolated, which would explode attribute cardinality.
 */
export interface RequestTelemetry {
  /** Route template with placeholders, e.g. `/v1/bootstrap/workspaces/{id}/feeds`. */
  route: string;
  method: string;
  /** Response-envelope field whose array length is the returned row count. */
  rowsField?: string;
  page?: number;
  pageSize?: number;
}

/** Resolves ambient attributes (boot phase, etc.) the request layer can't know itself. */
export type TelemetryContextProvider = () => Attributes;

export function startRequestSpan(info: RequestTelemetry, context?: Attributes): Span {
  return trace.getTracer(TRACER_NAME).startSpan(`${info.method} ${info.route}`, {
    attributes: {
      [TELEMETRY_CATEGORY_ATTRIBUTE]: "api",
      "http.request.method": info.method,
      "http.route": info.route,
      ...(info.page !== undefined ? { "http.request.page": info.page } : {}),
      ...(info.pageSize !== undefined ? { "http.request.page_size": info.pageSize } : {}),
      ...context,
    },
  });
}

export function endRequestSpanSuccess(span: Span, status: number | undefined, rows: number | undefined): void {
  if (status !== undefined) {
    span.setAttribute("http.response.status_code", status);
  }
  if (rows !== undefined) {
    span.setAttribute("http.response.rows", rows);
  }
  span.setStatus({ code: SpanStatusCode.OK });
  span.end();
}

export function endRequestSpanError(span: Span, error: unknown): void {
  const rejection = error as { status?: number; message?: string; error?: { message?: string } } | null;
  if (rejection?.status !== undefined) {
    span.setAttribute("http.response.status_code", rejection.status);
  }
  span.setStatus({
    code: SpanStatusCode.ERROR,
    message:
      (error instanceof Error ? error.message : (rejection?.error?.message ?? rejection?.message)) ??
      String(rejection?.status ?? error),
  });
  span.end();
}

/** Row count for a response envelope: the length of the named array field, when present. */
export function rowsFromResponse(data: unknown, rowsField: string | undefined): number | undefined {
  if (!rowsField || data == null || typeof data !== "object") {
    return undefined;
  }
  const rows = (data as Record<string, unknown>)[rowsField];
  return Array.isArray(rows) ? rows.length : undefined;
}
